//@flow
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';
import { PersonnelImportHelper } from './PersonnelImportHelper';
import { RoleId } from 'common/models/internal/RoleId';

const PERSONNEL_IMPORT_FORMAT: Array<TableColumnTypes> = [
    {
        titleKey: 'masterPersonnelProperties.id',
        id: 'id',
        disabled: true,
        fixed: true,
        required: true,
        templateValue: 'masterImportTemplateValues.masterPersonnel.id'
    },
    {
        titleKey: 'masterPersonnelProperties.code',
        id: 'code',
        disabled: true,
        templateValue: 'masterImportTemplateValues.masterPersonnel.code'
    },
    {
        titleKey: 'masterPersonnelProperties.name',
        id: 'name',
        required: true,
        templateValue: 'masterImportTemplateValues.masterPersonnel.name'
    },
    {
        titleKey: 'masterPersonnelProperties.customRole',
        id: 'customRole',
        templateValue: 'masterImportTemplateValues.masterPersonnel.customRole'
    },
    {
        titleKey: 'masterPersonnelProperties.language',
        id: 'language',
        templateValue: 'masterImportTemplateValues.masterPersonnel.language',
        validateCell: PersonnelImportHelper.validateLanguage
    },
    {
        titleKey: 'masterPersonnelProperties.personnelType',
        id: 'personnelType',
        templateValue:
            'masterImportTemplateValues.masterPersonnel.personnelType',
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) => {
            return isFieldHidden('personnelType', {
                personnelType,
                entitySettings
            });
        },
        required: true,
        processCell: PersonnelImportHelper.processPersonnelType,
        validateCell: PersonnelImportHelper.validatePersonelType
    },
    {
        titleKey: 'masterPersonnelProperties.transporterId',
        id: 'transporters',
        required: true,
        accessor: ['transporters'],
        templateValue:
            'masterImportTemplateValues.masterPersonnel.transporters',
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) => {
            return isFieldHidden('transporters', {
                personnelType,
                entitySettings
            });
        },
        processCell: PersonnelImportHelper.processTransporterIds,
        serializeCell: PersonnelImportHelper.serializeTransporterIds,
        getIdForAsyncValidation: transporterId => {
            return transporterId?.transporterId;
        },
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateTransporterIds,
            clientId
        }) => {
            PersonnelImportHelper.validateEntityIdAsync({
                validator: validateTransporterIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        }
    },
    {
        titleKey: 'masterPersonnelProperties.phoneNumber',
        id: 'phoneNumber',
        templateValue: 'masterImportTemplateValues.masterPersonnel.phoneNumber',
        accessor: ['phone', 'phoneNumber'],
        validateCell: PersonnelImportHelper.validatePhoneNumber
    },
    {
        titleKey: 'masterPersonnelProperties.emailAddress',
        id: 'emailAddress',
        required: true,
        templateValue:
            'masterImportTemplateValues.masterPersonnel.emailAddress',
        accessor: ['email', 'emailAddress'],
        validateCell: PersonnelImportHelper.validateEmailAddress
    },
    {
        titleKey: 'masterPersonnelProperties.hasAllTeamsAccess',
        id: 'hasAllTeamsAccess',
        templateValue:
            'masterImportTemplateValues.masterPersonnel.hasAllTeamsAccess',
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('hasAllTeamsAccess', {
                personnelType,
                entitySettings
            }),
        required: true,
        processCell: PersonnelImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterPersonnelProperties.teamId',
        id: 'teams',
        templateValue: 'masterImportTemplateValues.masterPersonnel.teams',
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('teams', { personnelType, entitySettings }),
        processCell: PersonnelImportHelper.processTeams,
        validateCell: PersonnelImportHelper.validateTeams,
        serializeCell: PersonnelImportHelper.serializeTeams
    },
    {
        titleKey: 'masterPersonnelProperties.roleMasterId',
        id: 'roleMasterId',
        accessor: ['roleMasterId'],
        processCell: PersonnelImportHelper.processRoleId,
        serializeCell: PersonnelImportHelper.serializeRoleId,
        getIdForAsyncValidation: (roleId: RoleId) => roleId.id,
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('roleMasterId', { personnelType, entitySettings }),
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateRoleIds,
            clientId
        }) => {
            PersonnelImportHelper.validateEntityIdAsync({
                validator: validateRoleIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        },
        required: true
    },
    {
        titleKey: 'masterPersonnelProperties.canManageEntities',
        id: 'canManageEntities',
        accessor: ['canManageEntities'],
        templateValue:
            'masterImportTemplateValues.masterPersonnel.canManageEntities',
        required: true,
        isHidden: ({ isFieldHidden, entitySettings, personnelType }) =>
            isFieldHidden('canManageEntities', {
                entitySettings,
                personnelType
            }),
        processCell: PersonnelImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterPersonnelProperties.canVerifyEntities',
        id: 'canVerifyEntities',
        accessor: ['canVerifyEntities'],
        templateValue:
            'masterImportTemplateValues.masterPersonnel.canVerifyEntities',
        required: true,
        isHidden: ({ isFieldHidden, entitySettings, personnelType }) =>
            isFieldHidden('canVerifyEntities', {
                entitySettings,
                personnelType
            }),
        processCell: PersonnelImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterPersonnelProperties.canManagePersonnel',
        id: 'canManagePersonnel',
        accessor: ['canManagePersonnel'],
        templateValue:
            'masterImportTemplateValues.masterPersonnel.canManagePersonnel',
        required: true,
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('canManagePersonnel', {
                personnelType,
                entitySettings
            }),
        processCell: PersonnelImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterPersonnelProperties.accessType',
        id: 'accessType',
        accessor: ['accessType'],
        templateValue: 'masterImportTemplateValues.masterPersonnel.accessType',
        required: true,
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('accessType', { personnelType, entitySettings }),
        processCell: PersonnelImportHelper.processAccessType
    },
    {
        titleKey: 'masterPersonnelProperties.timezone',
        id: 'timezone',
        templateValue: 'masterImportTemplateValues.masterPersonnel.timezone',
        isHidden: ({ localeSettings }) =>
            !localeSettings.multiTimeZoneSupportEnabled,
        validateCell: PersonnelImportHelper.validateTimezone
    }
];

export default PERSONNEL_IMPORT_FORMAT;
